import React, { useRef } from 'react'
import {
  useAccount,
  useActions,
  useCallback,
  useEffect,
  usePersistedStateV2,
  useSelector,
  useState,
} from 'src/hooks'
import {
  useDigitalCardOrderLineGiftCardAdd,
  useDigitalCardOrderLineGiftCardRemove,
  useDigitalCardOrderPay,
  useDigitalCardUpdate,
  useDigitalCardRecipientFromNameUpsert,
} from 'src/react_query/mutations/hooks'
import { LEGACY_SITE_URL } from 'src/app/api'
import { ActRoute } from 'src/act/routes/ActRoute'
import {
  queryKeyStore,
  useDigitalGiftCardProduct,
  useGetCard,
  useGetDigtalCardOrder,
  useGlobalSettings,
  useMarketingContent,
} from 'src/react_query'
import { ActSuccessRoute } from 'src/act/routes/ActSuccess'
import { LoadingSpinner, Text, Image } from '@sendoutcards/quantum-design-ui'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import { ShareScreen } from '../ShareScreen/ShareScreen'
import FourOFour from 'src/404/404'
import ActPreview from '../ActPreview/ActPreview'
import EditorCardColorBackground from '../ShareScreen/components/EditorCardColorBackground'
import ActSetupPanel, { SetupActions } from '../ActSetupPanel/ActSetupPanel'
import ActSetupEditRecipient from '../ActSetupEditRecipient/ActSetupEditRecipient'
import { AnimatePresence, motion } from 'motion/react'
import { PaymentMethod } from 'src/upsale/components/checkout/payment/PaymentMethod'
import ActSetupSubActionHeader from '../ActSetupPanel/ActSetupSubActionHeader'
import { isDesktop } from 'react-device-detect'
import { ExclusiveOfferDialog } from 'src/onboarding/components/CreateAccountWithSponsor/ExclusiveOfferDialog'
import GiftCardCatalog from '../GiftCard/GiftCardCatalog'
import { SelectableGiftCardType } from '../GiftCard/GiftCard'
import { ClientError } from 'graphql-request'
import { useRecaptcha } from 'src/hooks/useRecaptchaEnterprise'
import { useQueryClient } from '@tanstack/react-query'
import CreditCardCvcForm from 'src/payments/components/CreditCardCvcForm'

type ActSetupProps = {
  route: ActRoute
  cardId: string
}

const PANEL_VARIANTS = {
  enter: {
    opacity: 1,
    x: '0%',
    y: '0%',
    transition: {
      duration: 0.1,
      delay: 0.1,
    },
  },
  exit: {
    opacity: 1,
    x: '-100%',
    y: '0%',
    transition: {
      duration: 0.1,
    },
  },
}

const MOBILE_PANEL_VARIANTS = {
  enter: {
    opacity: 1,
    x: '0%',
    y: '0%',
    transition: {
      duration: 0.1,
      delay: 0.1,
    },
  },
  exit: {
    opacity: 1,
    x: '-100%',
    y: '0%',
    transition: {
      duration: 0.1,
      delay: 0.1,
    },
  },
}

const INNER_ACTION_VARIANTS = {
  enter: {
    opacity: 1,
    x: '0%',
    y: '0%',
    transition: {
      duration: 0.2,
      delay: 0.1,
    },
  },
  exit: {
    opacity: 1,
    x: '100%',
    y: '0%',
    transition: {
      duration: 0.1,
    },
  },
}

const ActSetup: React.FC<ActSetupProps> = props => {
  const { route, cardId } = props
  const actions = useActions()
  const account = useAccount()
  const queryClient = useQueryClient()
  const { data: marketingContent } = useMarketingContent()
  const { data: globalSettings } = useGlobalSettings()
  const isVerificationLevelFourEnabled =
    globalSettings?.verificationLevelFourEnabled ?? false
  const recaptcha = useRecaptcha(!isVerificationLevelFourEnabled)
  const isMobile = useSelector(state => state.window.width < 500)
  const isSparse = !account.username

  const baseURL = LEGACY_SITE_URL

  const digitalCardOrderPayMutation = useDigitalCardOrderPay()
  const updateDigitalCardMutation = useDigitalCardUpdate()
  const updateFromNameMutation = useDigitalCardRecipientFromNameUpsert()
  const addGiftCardMutation = useDigitalCardOrderLineGiftCardAdd()
  const removeGiftCardMutation = useDigitalCardOrderLineGiftCardRemove()
  const getCardQuery = useGetCard({ id: cardId }, { suspense: true })
  const card = getCardQuery.data?.card

  const { data: digitalCard } = useGetDigtalCardOrder(
    {
      id: card?.digitalCard?.pk ?? '',
    },
    { suspense: true },
  )

  const giftCard = digitalCard?.order?.data.items.find(
    item => item.name === 'Digital gift card',
  )
  const digitalGiftCardProduct = useDigitalGiftCardProduct({
    code: giftCard?.inner?.code ?? '',
  })

  const digitalGiftCard: SelectableGiftCardType | undefined =
    digitalGiftCardProduct.data && giftCard
      ? {
          title: digitalGiftCardProduct.data.title ?? giftCard.name,
          imageUrl: digitalGiftCardProduct.data.image ?? '',
          code: digitalGiftCardProduct.data.code,
          amount: giftCard.inner?.faceValue
            ? Number(giftCard.inner.faceValue) // This is a Decimal
            : giftCard.amount / 100, // This is an int converted to Decimal
          currency:
            giftCard.inner?.currency ??
            digitalGiftCardProduct.data.currency ??
            'USD',
        }
      : undefined

  const defaultActShareMessage = digitalGiftCard
    ? marketingContent?.defaultActGiftCardDefaultMessage.content
    : marketingContent?.defaultActShareMessage.content

  const [senderVerificationToken, setSenderVerificationToken] =
    usePersistedStateV2<string | undefined>(
      `user_auth_${account.id}`,
      undefined,
    )

  const [isLoading, setIsLoading] = useState(false)
  const [actTitle, setActTitle] = useState(
    digitalCard?.shareMessageTitle && digitalCard.shareMessageTitle !== ''
      ? digitalCard.shareMessageTitle
      : (card?.detailedSendableCard?.title ?? ''),
  )

  const fromPlaceHolder = account
    ? `${account.firstName ?? ''} ${account.lastName ?? ''}`
    : undefined

  const [senderName, setSenderName] = useState(fromPlaceHolder ?? '')

  const digitalCardToken =
    card?.digitalCard?.recipients && card.digitalCard?.recipients.length > 0
      ? card.digitalCard.recipients[0].token
      : undefined

  const isDigitalOrderPaid = digitalCard?.order?.isPaid

  const [shouldShowJoin, setShouldShowJoin] = useState(false)

  const [setupAction, setSetupAction] = useState<SetupActions>('Main')
  const [overrideSheetHeight, setOverrideSheetHeight] = useState<
    string | undefined
  >(undefined)

  const [shouldShowPreview, setShouldShowPreview] = useState(false)

  const [showPaymentAuthentication, setShowPaymentAuthentication] =
    useState(false)
  const [showCvcAuthentication, setShowCvcAuthentication] = useState(false)

  const recaptchaTokenRef = useRef<string>()
  const cvcTokenRef = useRef<string>()

  const resetAction = () => setSetupAction('Main')

  const hangleTogglePreview = () => {
    setShouldShowPreview(!shouldShowPreview)
  }

  const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(true)
  const [responseError, setResponseError] = useState<undefined | string>(
    undefined,
  )

  const hasRecipient = digitalCard?.recipients[0] !== undefined

  const getRecaptchaToken = useCallback(async (): Promise<
    string | undefined
  > => {
    const updateRecaptchaToken = async () => {
      const token =
        process.env.REACT_APP_RECAPTCHA_KEY && isVerificationLevelFourEnabled
          ? await recaptcha.getToken('verification_level_four')
          : undefined
      recaptchaTokenRef.current = token ?? undefined
      return token ?? undefined
    }

    return isVerificationLevelFourEnabled
      ? (recaptchaTokenRef.current ?? (await updateRecaptchaToken()))
      : undefined
  }, [recaptcha, recaptchaTokenRef, isVerificationLevelFourEnabled])

  const actOrderPay = async (jwt?: string) => {
    const digitalCardPk = card?.digitalCard?.pk
    const { total } = digitalCard?.order?.data ?? {}
    const hasPaymentSource = !!account?.stripeSource
    const canProceedWithPayment = total === 0 || hasPaymentSource
    if (
      digitalCardPk &&
      !!digitalCard?.order &&
      canProceedWithPayment &&
      hasRecipient
    ) {
      try {
        setIsLoading(true)
        setResponseError(undefined)
        const recaptchaToken = await getRecaptchaToken()
        await updateDigitalCardMutation.mutateAsync({
          digitalCardPk: digitalCardPk,
          input: {
            shareMessageTitle: actTitle,
          },
        })
        const digiCard = await digitalCardOrderPayMutation.mutateAsync({
          digitalCardPk: digitalCardPk,
          jwt: jwt ?? senderVerificationToken,
          recaptchaToken: recaptchaToken,
          cvcToken: cvcTokenRef.current,
        })
        if (digiCard.recipients.length > 0 && digiCard.recipients[0].token) {
          actions.openActSuccess(cardId)
        }
      } catch (error) {
        let hasUnknownError = false

        const errorsToPrint = []

        if (error instanceof ClientError && error.response.errors) {
          for (const e of error.response.errors) {
            if (
              e?.extensions?.['code'] ===
              'DIGITAL-CARD-ORDER-PAY-AUTHENTICATION-NEEDED'
            ) {
              setShowPaymentAuthentication(true)
            } else if (
              e?.extensions?.['code'] === 'DIGITAL-CARD-ORDER-PAY-CVC-NEEDED'
            ) {
              setShowCvcAuthentication(true)
            } else if (
              e?.extensions?.['code'] ===
              'DIGITAL-CARD-ORDER-PAY-INVALID-RECAPTCHA'
            ) {
              recaptchaTokenRef.current = undefined
              actOrderPay(jwt)
              errorsToPrint.push(e)
            } else {
              hasUnknownError = true
              errorsToPrint.push(e)
            }
          }

          if (errorsToPrint) {
            console.error(error)
            const messages = errorsToPrint.map(value => value.message) ?? []
            const msgStr = messages.join('')
            setResponseError(msgStr)
          }
        } else {
          hasUnknownError = true

          console.error(error)
          const msgStr =
            error instanceof Error ? error.message : 'Payment Failed'
          setResponseError(msgStr)
        }

        // It's hard to identify CVC token errors, so if we don't know why it failed we invalidate the CVC
        if (hasUnknownError) {
          cvcTokenRef.current = undefined
        }
      } finally {
        setIsLoading(false)
      }
    }
  }

  const successRecipientName =
    card?.digitalCard &&
    card?.digitalCard.recipients &&
    card?.digitalCard.recipients.length > 0 &&
    card?.digitalCard.recipients[0].contactRequest
      ? card.digitalCard.recipients[0].contactRequest.firstName
      : 'their'

  const handleEditRecipientBack = () => {
    resetAction()
  }

  const handleAddGiftCard = async (
    selectedGiftCard: SelectableGiftCardType,
  ) => {
    const digitalCardPk = card?.digitalCard?.pk
    if (digitalCardPk) {
      if (digitalGiftCard) {
        handleRemoveGiftCard(digitalGiftCard, false)
      }
      await addGiftCardMutation.mutateAsync({
        digitalCardPk: digitalCardPk,
        code: selectedGiftCard.code,
        amount: selectedGiftCard.amount.toFixed(2),
        currency: selectedGiftCard.currency,
      })
      resetAction()
    }
  }

  const handleRemoveGiftCard = async (
    selectedGiftCard: SelectableGiftCardType,
    shouldNotResetAction?: boolean,
  ) => {
    const digitalCardPk = card?.digitalCard?.pk
    if (digitalCardPk) {
      await removeGiftCardMutation.mutateAsync({
        digitalCardPk: digitalCardPk,
        code: selectedGiftCard.code,
        amount: selectedGiftCard.amount.toFixed(2),
        currency: selectedGiftCard.currency,
      })

      if (!shouldNotResetAction) resetAction()
    }
  }

  useEffect(() => {
    if (isDigitalOrderPaid) {
      actions.openActSuccess(cardId)
    }
  }, [isDigitalOrderPaid, actions, cardId])

  useEffect(() => {
    if (!!digitalCard?.order?.expiresAt) {
      const now = new Date()
      const expiresAt = new Date(digitalCard?.order?.expiresAt)
      const expiresIn = expiresAt.valueOf() - now.valueOf()
      window.setTimeout(() => {
        if (!!queryClient && !!queryKeyStore && !!digitalCard) {
          queryClient.invalidateQueries(
            queryKeyStore.digital_card_order.detail({ id: digitalCard.pk }),
          )
        }
      }, expiresIn)
    }
  }, [digitalCard, queryClient])

  if (!route.subroute) {
    return null
  }

  if (!card) {
    return <FourOFour />
  }

  return (
    <>
      {isLoading ? (
        <Dialog
          mobileBreakPointOverride={799}
          maxWidth={isMobile ? undefined : '360px'}
          mobileOpenHeight="0px"
          isOpen={true}
          onClose={() => {
            // what to do here
          }}
          outerWrapperStyle={{ display: 'flex', flexDirection: 'column' }}
          wrapperStyle={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: '1',
            overflowX: 'auto',
          }}
          titleBarReplacement={null}
          padding="20px"
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '20px',
            }}
          >
            <Image
              isActive
              image={{ url: card.frontPreviewUrl }}
              width={card.isHorizontal ? '210px' : '150px'}
              height={card.isHorizontal ? '150px' : '210px'}
            />
            <Text
              content="Generating Shareable Card"
              type="largeBody"
              color="primaryHeading"
              weight="semiBold"
            />
            <LoadingSpinner size="medium" />
          </div>
        </Dialog>
      ) : shouldShowJoin ? (
        <ExclusiveOfferDialog
          onFinishedJoin={() => actions.openAccount()}
          onClose={() =>
            actions.openAccount({ shouldDisplayDashboardSplash: '' })
          }
        />
      ) : (
        <>
          {route.subroute.path === ActSuccessRoute.path ? (
            <div>
              <EditorCardColorBackground card={card} />
              {digitalCardToken && (
                <ShareScreen
                  recipientName={successRecipientName}
                  card={card}
                  baseURL={baseURL}
                  isSparse={isSparse}
                  token={digitalCardToken}
                  giftCard={giftCard}
                  onCopyToClipboard={() => {
                    try {
                      navigator.clipboard.writeText(
                        `${defaultActShareMessage}\n${baseURL}/act/${digitalCardToken}`,
                      )
                      return true
                    } catch (err) {
                      console.warn('Clipboard copy failed:', err)
                      return false
                    }
                  }}
                  onFinish={() => {
                    if (isSparse) {
                      setShouldShowJoin(true)
                    } else {
                      actions.openAccount({ shouldDisplayDashboardSplash: '' })
                    }
                  }}
                />
              )}
            </div>
          ) : (
            <ActPreview
              card={card}
              shouldShowPreview={shouldShowPreview}
              togglePreviewOrder={hangleTogglePreview}
              mobileSetupPanelHeight={
                setupAction === 'AddGiftCard' &&
                !isDesktop &&
                !!!overrideSheetHeight
                  ? '550px'
                  : setupAction === 'AddGiftCard' &&
                      !isDesktop &&
                      overrideSheetHeight
                    ? overrideSheetHeight
                    : undefined
              }
            >
              <AnimatePresence>
                {setupAction === 'Main' && (
                  <motion.div
                    key="mainAction"
                    initial="exit"
                    exit="exit"
                    animate="enter"
                    variants={
                      isDesktop ? PANEL_VARIANTS : MOBILE_PANEL_VARIANTS
                    }
                    style={{ height: '100%' }}
                  >
                    <ActSetupPanel
                      errorMsg={responseError}
                      digitalCard={digitalCard}
                      digitalCardOrder={digitalCard?.order}
                      title={{ value: actTitle, onChangeTitle: setActTitle }}
                      isLoadingGiftCard={digitalGiftCardProduct.isFetching}
                      giftCard={digitalGiftCard}
                      fromName={{
                        defaultPlaceholder: fromPlaceHolder,
                        value: senderName,
                        onChangeFromName: (newName: string) =>
                          setSenderName(newName),
                        onFinishEdit: async () => {
                          const [senderFirstName, ...rest] = senderName
                            .trim()
                            .split(' ')
                          const senderLastName = rest.join(' ')
                          const digitalCardPk = card?.digitalCard?.pk

                          if (!digitalCardPk) return

                          await updateFromNameMutation.mutateAsync({
                            digitalCardPk,
                            senderFirstName,
                            senderLastName,
                          })
                        },
                      }}
                      isPaymentAuthenticationOpen={showPaymentAuthentication}
                      onClosePaymentAuthentication={() => {
                        // Display a dialog explaining that the user
                        // need to authorize the payment by
                        // verifying with their phone number
                        // the autencity of the action
                        setShowPaymentAuthentication(false)
                      }}
                      onSetPaymentAuthentication={jwt => {
                        // Save Sender Authentication
                        setSenderVerificationToken(jwt)
                        setShowPaymentAuthentication(false)
                        // Try to pay again, now authorized with the JWT
                        actOrderPay(jwt)
                      }}
                      onSend={() => actOrderPay()}
                      onSetupAction={setSetupAction}
                      onTogglePreview={hangleTogglePreview}
                      isPreviewOpen={shouldShowPreview}
                    />
                    {showCvcAuthentication && (
                      <Dialog
                        isOpen={true}
                        onClose={() => setShowCvcAuthentication(false)}
                        titleBarBackgroundColor="#FFF"
                        sheetBgColorOverride="#EEEEEE"
                        maxWidth="25rem"
                      >
                        <CreditCardCvcForm
                          onSuccess={token => {
                            setShowCvcAuthentication(false)
                            cvcTokenRef.current = token.id
                            actOrderPay()
                          }}
                        />
                      </Dialog>
                    )}
                  </motion.div>
                )}
                {setupAction === 'EditRecipient' && (
                  <motion.div
                    key="recipientAction"
                    initial="exit"
                    exit="exit"
                    animate="enter"
                    variants={INNER_ACTION_VARIANTS}
                    style={{ height: '100%' }}
                  >
                    <ActSetupEditRecipient
                      onBack={handleEditRecipientBack}
                      hasActOrder={!!digitalCard?.order}
                      digitalCardPk={digitalCard?.pk}
                    />
                  </motion.div>
                )}
                {setupAction === 'EditPaymentMethod' && (
                  <motion.div
                    key="recipientAction"
                    initial="exit"
                    exit="exit"
                    animate="enter"
                    variants={INNER_ACTION_VARIANTS}
                  >
                    <ActSetupSubActionHeader
                      title="Add Credit Card:"
                      onBack={resetAction}
                    />
                    <div style={{ padding: '16px' }}>
                      <PaymentMethod
                        paymentFormDisplay={{
                          shouldShowPaymentForm: showPaymentMethodForm,
                          setShouldShowPaymentMethodForm:
                            setShowPaymentMethodForm,
                        }}
                        cardHolderName={`${account.firstName} ${account.lastName}`}
                        paymentMethod={account.stripeSource ?? undefined}
                        onFinish={resetAction}
                        handlePaymentMethod={() =>
                          setShowPaymentMethodForm(true)
                        }
                      />
                    </div>
                  </motion.div>
                )}
                {setupAction === 'AddGiftCard' && (
                  <motion.div
                    key="addGiftAction"
                    initial="exit"
                    exit="exit"
                    animate="enter"
                    variants={INNER_ACTION_VARIANTS}
                    style={{
                      maxWidth: '100dvw',
                      overflow: 'hidden',
                      height: '100%',
                    }}
                  >
                    <GiftCardCatalog
                      currentlyAddedGiftCard={digitalGiftCard}
                      onSelect={handleAddGiftCard}
                      onRemove={handleRemoveGiftCard}
                      setMobileSheetHeight={setOverrideSheetHeight}
                      onBack={() => {
                        resetAction()
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </ActPreview>
          )}
        </>
      )}
    </>
  )
}

export default ActSetup
